import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Loan } from '@/hooks/useSupabaseStore';

const statusLabels: Record<string, string> = {
  active: 'Đang vay',
  overdue: 'Quá hạn',
  due_soon: 'Sắp đến hạn',
  interest_paid: 'Đã đóng lãi',
  redeemed: 'Đã chuộc',
  forfeited: 'Tịch thu',
};

export type ExportFormat = 'excel' | 'pdf';

interface SummaryRow {
  label: string;
  value: string;
}

function buildSummary(loans: Loan[]): SummaryRow[] {
  const totalValue = loans.reduce((sum, l) => sum + l.loanAmount, 0);

  const statusCounts: Record<string, number> = {};
  loans.forEach((l) => {
    const label = statusLabels[l.status] || l.status;
    statusCounts[label] = (statusCounts[label] || 0) + 1;
  });

  const categoryCounts: Record<string, number> = {};
  loans.forEach((l) => {
    categoryCounts[l.assetCategory] = (categoryCounts[l.assetCategory] || 0) + 1;
  });

  const rows: SummaryRow[] = [
    { label: 'Tổng số khoản vay', value: String(loans.length) },
    { label: 'Tổng giá trị vay', value: `${totalValue.toLocaleString('vi-VN')} đ` },
    { label: '--- Theo trạng thái ---', value: '' },
    ...Object.entries(statusCounts).map(([label, count]) => ({
      label: `  – ${label}`,
      value: `${count} khoản`,
    })),
    { label: '--- Theo danh mục ---', value: '' },
    ...Object.entries(categoryCounts).map(([label, count]) => ({
      label: `  – ${label}`,
      value: `${count} cái`,
    })),
  ];

  return rows;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function useExportLoans() {
  const [exporting, setExporting] = useState(false);

  const exportLoans = useCallback(
    async (loans: Loan[], format: ExportFormat) => {
      if (loans.length === 0) {
        alert('Không có dữ liệu để xuất!');
        return;
      }

      setExporting(true);

      const rows = loans.map((loan) => ({
        'Mã KV': loan.id,
        'Khách hàng': loan.customerName,
        'Số điện thoại': loan.customerPhone,
        'Tài sản': loan.assetName,
        'Danh mục': loan.assetCategory,
        'Giá trị vay': loan.loanAmount,
        'Lãi suất': `${loan.interestRate}%/tháng`,
        'Ngày vay': loan.startDate,
        'Hạn trả': loan.dueDate,
        'Trạng thái': statusLabels[loan.status] || loan.status,
      }));

      const summary = buildSummary(loans);
      const filename = `Danh_sach_khoan_vay_${new Date().toISOString().slice(0, 10)}`;

      try {
        if (format === 'excel') {
          const wb = XLSX.utils.book_new();

          // Sheet 1: Danh sách khoản vay
          const ws = XLSX.utils.json_to_sheet(rows);
          const wscols = [
            { wch: 14 },
            { wch: 20 },
            { wch: 14 },
            { wch: 24 },
            { wch: 12 },
            { wch: 14 },
            { wch: 12 },
            { wch: 18 },
            { wch: 12 },
            { wch: 12 },
          ];
          ws['!cols'] = wscols;
          XLSX.utils.book_append_sheet(wb, ws, 'Khoản vay');

          // Sheet 2: Tổng kết
          const summarySheet = XLSX.utils.json_to_sheet(summary);
          summarySheet['!cols'] = [{ wch: 28 }, { wch: 22 }];
          XLSX.utils.book_append_sheet(wb, summarySheet, 'Tổng kết');

          const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          downloadBlob(blob, `${filename}.xlsx`);
        } else {
          // PDF
          const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

          doc.setFontSize(16);
          doc.text('DANH SÁCH KHOẢN VAY', 148, 14, { align: 'center' });
          doc.setFontSize(10);
          doc.text(
            `Ngày xuất: ${new Date().toLocaleDateString('vi-VN')}`,
            148,
            20,
            { align: 'center' }
          );

          const headers = [
            [
              'Mã KV',
              'Khách hàng',
              'SĐT',
              'Tài sản',
              'Danh mục',
              'Giá trị vay',
              'Lãi suất',
              'Ngày vay',
              'Hạn trả',
              'Trạng thái',
            ],
          ];

          const body = rows.map((r) => [
            r['Mã KV'],
            r['Khách hàng'],
            r['Số điện thoại'],
            r['Tài sản'],
            r['Danh mục'],
            r['Giá trị vay'].toLocaleString('vi-VN'),
            r['Lãi suất'],
            r['Ngày vay'],
            r['Hạn trả'],
            r['Trạng thái'],
          ]);

          autoTable(doc, {
            head: headers,
            body,
            startY: 26,
            styles: { fontSize: 9, cellPadding: 2 },
            headStyles: {
              fillColor: [245, 158, 11],
              textColor: 255,
              fontStyle: 'bold',
            },
            alternateRowStyles: { fillColor: [250, 250, 250] },
            margin: { left: 10, right: 10 },
            theme: 'grid',
          });

          // Bảng tổng kết ở cuối
          const finalY =
            (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY || 80;

          doc.setFontSize(12);
          doc.text('TỔNG KẾT', 10, finalY + 10);

          autoTable(doc, {
            body: summary.map((s) => [s.label, s.value]),
            startY: finalY + 14,
            styles: { fontSize: 10, cellPadding: 2 },
            columnStyles: {
              0: { fontStyle: 'bold' },
            },
            alternateRowStyles: { fillColor: [250, 250, 250] },
            margin: { left: 10, right: 10 },
            theme: 'grid',
            head: [],
          });

          const pdfBlob = doc.output('blob');
          downloadBlob(pdfBlob, `${filename}.pdf`);
        }
      } catch (err) {
        console.error('Export error:', err);
        alert('Xuất file thất bại, vui lòng thử lại.');
      } finally {
        setExporting(false);
      }
    },
    []
  );

  return { exportLoans, exporting };
}